export const isIOSDocument = () =>
  typeof document !== "undefined" &&
  document.documentElement.classList.contains("os-ios");

let iosKeyboardBridge: HTMLTextAreaElement | null = null;

const getIOSKeyboardBridge = () => {
  if (iosKeyboardBridge?.isConnected) {
    return iosKeyboardBridge;
  }

  const bridge = document.createElement("textarea");

  bridge.setAttribute("aria-hidden", "true");
  bridge.setAttribute("autocomplete", "off");
  bridge.setAttribute("autocapitalize", "off");
  bridge.setAttribute("spellcheck", "false");
  bridge.tabIndex = -1;
  bridge.style.position = "fixed";
  bridge.style.left = "0";
  bridge.style.bottom = "0";
  bridge.style.width = "1px";
  bridge.style.height = "1px";
  bridge.style.padding = "0";
  bridge.style.border = "0";
  bridge.style.opacity = "0";
  bridge.style.fontSize = "16px";
  bridge.style.pointerEvents = "none";
  bridge.style.zIndex = "-1";

  document.body.appendChild(bridge);
  iosKeyboardBridge = bridge;

  return bridge;
};

export const focusIOSKeyboardBridge = () => {
  if (!isIOSDocument()) {
    return false;
  }

  const bridge = getIOSKeyboardBridge();

  bridge.focus({
    preventScroll: true,
  });

  return document.activeElement === bridge;
};

const editorEditableSelectors = [
  ".ProseMirror[contenteditable='true']",
  "[contenteditable='true']",
];

export const isEditorBranchVisible = (element: HTMLElement) => {
  let currentElement: HTMLElement | null = element;

  while (currentElement) {
    if (
      currentElement.hidden ||
      currentElement.inert ||
      currentElement.getAttribute("aria-hidden") === "true"
    ) {
      return false;
    }

    const { display, opacity, visibility } =
      window.getComputedStyle(currentElement);

    if (
      display === "none" ||
      visibility === "hidden" ||
      visibility === "collapse" ||
      opacity === "0"
    ) {
      return false;
    }

    currentElement = currentElement.parentElement;
  }

  return true;
};

export const getEditorEditableElement = (root: ParentNode = document) => {
  const candidates = editorEditableSelectors.flatMap((selector) =>
    Array.from(root.querySelectorAll<HTMLElement>(selector)),
  );

  return candidates.find(isEditorBranchVisible) ?? candidates[0] ?? null;
};

export const focusEditorEditableElement = (
  root: ParentNode = document,
  {
    dispatchClick = false,
    preventScroll = true,
  }: {
    dispatchClick?: boolean;
    preventScroll?: boolean;
  } = {},
) => {
  const editableElement = getEditorEditableElement(root);

  if (dispatchClick && editableElement) {
    editableElement.dispatchEvent(
      new MouseEvent("click", {
        bubbles: true,
        cancelable: true,
        view: window,
      }),
    );
  }

  editableElement?.focus({
    preventScroll,
  });

  return Boolean(
    editableElement &&
    (document.activeElement === editableElement ||
      editableElement.contains(document.activeElement)),
  );
};

export const focusInlineReplyComposer = (
  postCacheKey: string,
  options?: {
    iosActivation?: boolean;
  },
) => {
  const replyEditor = Array.from(
    document.querySelectorAll<HTMLElement>("[data-inline-reply-composer]"),
  ).find((element) => element.dataset.inlineReplyComposer === postCacheKey);

  if (!replyEditor) {
    return false;
  }

  return focusEditorEditableElement(replyEditor, {
    dispatchClick: options?.iosActivation,
    preventScroll: !options?.iosActivation,
  });
};
